import { AnalyticsContext } from '../../../../Pages/GMS/Performance/Context/AnalyticsContext';
import AuthManager from "Common/AuthManager";
import BootBoxManager from "Common/BootBoxManager";
import PermissionsManager from "Common/PermissionsManager";
import Utils from "Common/Utils";
import { AppContext } from "Context/AppContext";
import ServiceWrapper from "Services/ServiceWrapper";
import AppSettings from "Settings/AppSettings";
import React, { useContext, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { IIndustryList, IParty, IPartyList } from "../types";
import { getMerchantsBasedOnPartyAndIndustry } from '../APIServices/GMSServices';
import { IRefreshHierarchy } from 'types/Context/IRefreshHierarchy';

const useSearchBar = () => {
    const history = useHistory();

    var [partyList, setPartyList] = React.useState<IPartyList>([]);
    var [industryList, setIndustryList] = React.useState<IIndustryList>([]);
    const appData = useContext<any>(AppContext);
    const analyticsContext = useContext<any>(AnalyticsContext);
    
    const [parentChannelList, setParentChannelList] = React.useState<any[]>([]);
    const [selectedParentChannel, setSelectedParentChannel] = React.useState<any>(null);
    const [environmentList, setEnvironmentList] = React.useState<any[]>([]);
    const [selectedEnvironment, setSelectedEnvironment] = React.useState<any>(null);
    
    const [channelList, setChannelList] = React.useState([])
    const [selectedChannel, setSelectedChannel] = React.useState('')

    const [isLoadingParty, setIsLoadingParty] = React.useState(false);
    const [isLoadingIndustry, setIsLoadingIndustry] = React.useState(false);
    const [isLoadingChannel, setIsLoadingChannel] = React.useState(false);
    const [isLoadingEnvironment, setIsLoadingEnvironment] = React.useState(false);
    
    const requestVersions = React.useRef(new RequestVersionManager());
    
    const operationInProgress = React.useRef({
        party: false,
        industry: false,
        channel: false,
        environment: false
    });
    
    const isManualPartyChange = React.useRef(false);
    const isManualIndustryChange = React.useRef(false);
    const isInitialLoad = React.useRef(true);
    const isSwitchingWorkspace = React.useRef(false);
    
    const current_party_id = JSON.parse(localStorage?.getItem("current_party_id") as string)
    const industryId = appData.get_selected_industry() && appData?.get_selected_industry()?.id;

    const refreshParties = async (): Promise<void> => {
        const api_base_url = AppSettings.GMS_BACKEND_API_URL;
        
        try {
            let parties = await ServiceWrapper.doGet(
                api_base_url + "user/party/active/list",
                null,
                null,
                AuthManager.getGmsToken()
            );
            
            
            if (parties.errorfound || !parties.data?.data) {
                console.error('[refreshParties] Error or no data in response');
                return;
            }
            
            const tplist = parties.data.data;
            if (tplist.length <= 0) {
                console.log('[refreshParties] Empty party list');
                return;
            }
            
            setPartyList(tplist);
            
            const currentPartyId = appData.selectedParty?.id || localStorage.getItem("party");
            
            if (currentPartyId) {
                const partyStillExists = tplist.find((p: any) => p.id === currentPartyId);
                
                if (partyStillExists) {
                    appData.setSelectedParty(partyStillExists);
                    localStorage.setItem("party", partyStillExists.id);
                    await refreshDomains(partyStillExists.id);
                } else {
                    const firstParty = tplist[0];
                    appData.setSelectedParty(firstParty);
                    localStorage.setItem("party", firstParty.id);
                    await refreshDomains(firstParty.id);
                }
            } else {
                const firstParty = tplist[0];
                appData.setSelectedParty(firstParty);
                localStorage.setItem("party", firstParty.id);
                await refreshDomains(firstParty.id);
            }
        } catch (error) {
            console.error('[refreshParties] Error:', error);
        }
    };

    const refreshDomains = async (partyId?: string): Promise<void> => {
        const targetPartyId = partyId || appData.selectedParty?.id;
        
        if (!targetPartyId) {
            console.log('[refreshDomains] No party ID, returning');
            return;
        }
        
        const api_base_url = AppSettings.GMS_BACKEND_API_URL;
        
        try {
            let industryResponse = await ServiceWrapper.doGet(
                api_base_url + "party/" + targetPartyId + "/industry/list/active",
                null,
                null,
                AuthManager.getGmsToken()
            );
            
            
            if (industryResponse.errorfound || !industryResponse.data?.data) {
                console.log('[refreshDomains] Error or no data');
                return;
            }
            
            const tplist = industryResponse.data.data;
            
            if (!tplist || tplist.length === 0) {
                setIndustryList([]);
                appData.setSelectedIndustry(null);
                setParentChannelList([]);
                setEnvironmentList([]);
                setSelectedParentChannel(null);
                setSelectedEnvironment(null);
                localStorage.removeItem("industry");
                localStorage.removeItem("selectedParentChannel");
                localStorage.removeItem("selectedEnvironment");
                return;
            }
            
            setIndustryList(tplist);
            
            let currentIndustryId = appData.selectedIndustry?.id;
            if (!currentIndustryId && localStorage.getItem("industry")) {
                try {
                    const parsed = JSON.parse(localStorage.getItem("industry")!.replace(/\\/g, ""));
                    currentIndustryId = parsed.id;
                } catch (e) {
                    console.error('[refreshDomains] Invalid industry JSON in localStorage:', e);
                }
            }
            
            let domainToSelect = null;
            if (currentIndustryId) {
                domainToSelect = tplist.find((i: any) => i.id === currentIndustryId);
            }
            
            if (!domainToSelect) {
                domainToSelect = tplist[0];
            }
            
            appData.update_industry(domainToSelect);
            localStorage.setItem("industry", JSON.stringify(domainToSelect));
            
            await refreshChannels(targetPartyId, domainToSelect.id);
        } catch (error) {
            console.error('[refreshDomains] Error:', error);
        }
    };

    const refreshChannels = async (partyId?: string, domainId?: string): Promise<void> => {
        const targetPartyId = partyId || appData.selectedParty?.id;
        const targetDomainId = domainId || appData.selectedIndustry?.id;
        
        
        if (!targetPartyId || !targetDomainId) {
            console.log('[refreshChannels] Missing party or domain ID');
            return;
        }
        
        try {
            const parentResponse = await ServiceWrapper.doGet(
                `${AppSettings.GMS_BACKEND_API_URL}list/party/${targetPartyId}/industry/${targetDomainId}/parent_channel`,
                null,
                null,
                AuthManager.getGmsToken()
            );
            
            const response = parentResponse?.data;
            
            if (!response || !response.data || !Array.isArray(response.data)) {
                setParentChannelList([]);
                setSelectedParentChannel(null);
                setEnvironmentList([]);
                setSelectedEnvironment(null);
                localStorage.removeItem("selectedParentChannel");
                localStorage.removeItem("selectedEnvironment");
                return;
            }
            
            setParentChannelList(response.data);
            
            if (response.data.length === 0) {
                setSelectedParentChannel(null);
                setEnvironmentList([]);
                setSelectedEnvironment(null);
                localStorage.removeItem("selectedParentChannel");
                localStorage.removeItem("selectedEnvironment");
                return;
            }
            
            let currentChannelId = selectedParentChannel?.id;
            if (!currentChannelId && localStorage.getItem("selectedParentChannel")) {
                try {
                    const parsed = JSON.parse(localStorage.getItem("selectedParentChannel")!);
                    currentChannelId = parsed.id;
                } catch (e) {
                    console.error('[refreshChannels] Invalid channel JSON in localStorage:', e);
                }
            }
            
            let channelToSelect = null;
            if (currentChannelId) {
                channelToSelect = response.data.find((c: any) => c.id === currentChannelId);
            }
            
            if (!channelToSelect) {
                channelToSelect = response.data[0];
            }
            
            setSelectedParentChannel(channelToSelect);
            localStorage.setItem("selectedParentChannel", JSON.stringify(channelToSelect));
            appData.setSelectedChannel?.(channelToSelect);
            await refreshEnvironments(channelToSelect.id);
        } catch (error) {
            console.error('[refreshChannels] Error:', error);
        }
    };

    const refreshEnvironments = async (channelId?: string): Promise<void> => {
        const targetChannelId = channelId || selectedParentChannel?.id;
        const targetPartyId = appData.selectedParty?.id;
        
        if (!targetChannelId || !targetPartyId) {
            return;
        }
        
        try {
            const environementResponse = await ServiceWrapper.doGet(
                `${AppSettings.GMS_BACKEND_API_URL}list/party/${targetPartyId}/parent_channel/${targetChannelId}/channel`,
                null,
                null,
                AuthManager.getGmsToken()
            );
            
            const response = environementResponse?.data;
            
            if (!response || !response.data || !Array.isArray(response.data)) {
                setEnvironmentList([]);
                setSelectedEnvironment(null);
                localStorage.removeItem("selectedEnvironment");
                appData.setChannelKey('');
                return;
            }
            
            setEnvironmentList(response.data);
            
            if (response.data.length === 0) {
                setSelectedEnvironment(null);
                localStorage.removeItem("selectedEnvironment");
                appData.setChannelKey('');
                return;
            }
            
            let currentEnvId = selectedEnvironment?.id;
            if (!currentEnvId && localStorage.getItem("selectedEnvironment")) {
                try {
                    const parsed = JSON.parse(localStorage.getItem("selectedEnvironment")!);
                    currentEnvId = parsed.id;
                } catch (e) {
                    console.error('[refreshEnvironments] Invalid environment JSON in localStorage:', e);
                }
            }
            
            let envToSelect = null;
            if (currentEnvId) {
                envToSelect = response.data.find((e: any) => e.id === currentEnvId);
            }
            
            if (!envToSelect) {
                envToSelect = response.data[0];
            }
            
            setSelectedEnvironment(envToSelect);
            localStorage.setItem("selectedEnvironment", JSON.stringify(envToSelect));
            appData.setSelectedEnvironment?.(envToSelect);
            appData.setChannelKey(envToSelect?.key ?? '');
        } catch (error) {
            console.error('[refreshEnvironments] Error:', error);
        }
    };


    const refreshAll = async (): Promise<void> => {
        requestVersions.current.clearAll();
        
        const api_base_url = AppSettings.GMS_BACKEND_API_URL;
        let parties = await ServiceWrapper.doGet(
            api_base_url + "user/party/active/list",
            null,
            null,
            AuthManager.getGmsToken()
        );
        
        if (parties.errorfound || !parties.data?.data || parties.data.data.length <= 0) {
            console.log('[refreshAll] No parties found');
            return;
        }
        
        const tplist = parties.data.data;
        setPartyList(tplist);
        
        let selectedParty = tplist[0];
        const storedPartyId = localStorage.getItem("party");
        
        if (storedPartyId) {
            const found = tplist.find((p: any) => p.id === storedPartyId);
            if (found) {
                selectedParty = found;
            }
        }
        
        appData.setSelectedParty(selectedParty);
        localStorage.setItem("party", selectedParty.id);
        await refreshDomains(selectedParty.id);
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const currentIndustry = appData.selectedIndustry || JSON.parse(localStorage.getItem("industry") || "null");
        if (currentIndustry) {
            await refreshChannels(selectedParty.id, currentIndustry.id);
        }
        
        const currentPath = window.location.pathname;
        
        if (currentPath === "/workspace/preparing" || currentPath === "/") {
            const industry = appData.selectedIndustry;
            const storedChannel = localStorage.getItem("selectedParentChannel");
            const storedEnv = localStorage.getItem("selectedEnvironment");
            
            let channel = selectedParentChannel;
            let env = selectedEnvironment;
            
            if (storedChannel) {
                try {
                    channel = JSON.parse(storedChannel);
                } catch (e) {}
            }
            
            if (storedEnv) {
                try {
                    env = JSON.parse(storedEnv);
                } catch (e) {}
            }
            
            if (industry) {
                await switchWorkspace(
                    selectedParty.id,
                    industry.id,
                    channel?.id || '',
                    env?.id || '',
                    "Loading workspace..."
                );
                
                history.push("/gms/performance");
            }
        }
    };

    React.useEffect(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const isBrowserRefresh = navigation && navigation.type === 'reload';
        
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            refreshAll();
        }
    }, []);

    useEffect(() => {
        const refreshFunctions: IRefreshHierarchy = {
            refreshParties,
            refreshDomains,
            refreshChannels,
            refreshEnvironments,
            refreshAll
        };
        
        appData.setRefreshHierarchy(refreshFunctions);
    }, [selectedParentChannel, selectedEnvironment, partyList, industryList, parentChannelList, environmentList]);

    const validateStoredSelections = () => {
        const storedParty = localStorage.getItem("party");
        const storedIndustry = localStorage.getItem("industry");
        const storedChannel = localStorage.getItem("selectedParentChannel");
        const storedEnv = localStorage.getItem("selectedEnvironment");
        
        if (storedEnv && !storedChannel) {
            localStorage.removeItem("selectedEnvironment");
        }
        
        if (storedChannel && !storedIndustry) {
            localStorage.removeItem("selectedParentChannel");
            localStorage.removeItem("selectedEnvironment");
        }
        
        if (storedIndustry && !storedParty) {
            localStorage.removeItem("industry");
            localStorage.removeItem("selectedParentChannel");
            localStorage.removeItem("selectedEnvironment");
        }
    };
    
    React.useEffect(() => {
        validateStoredSelections();
    }, []);
    
    function capitalizeFirstLetter(string: string) {
        return string ? string.charAt(0).toUpperCase() + string.slice(1) : "";
    }
    
    const getUserName = () => {
        let userObj = null;
        if (AuthManager.isAuthenticated()) userObj = AuthManager.getAuthUser();
        if (!userObj?.profile) return capitalizeFirstLetter("guest");
        appData.setUserName(capitalizeFirstLetter(userObj.profile.first_name) ?? '');
        return capitalizeFirstLetter(userObj.profile.first_name);
    };

    const getPartyList = async (skipWorkspaceSwitch: boolean = false) => {
        
        let _partyList: IPartyList;
        let module = localStorage.getItem("module");
        
        if (window.location.pathname === "/workspace/preparing") {
            history.push("/workspace/preparing");
        } else {
            history.push(module ?? window.location.pathname);
        }
        
        const api_base_url = AppSettings.GMS_BACKEND_API_URL;
        let parties = await ServiceWrapper.doGet(
            api_base_url + "user/party/active/list",
            null,
            null,
            AuthManager.getGmsToken()
        );
        
        if (parties.errorfound) {
            console.log('[getPartyList] Error found in response');
            return;
        }

        var tplist = parties.data.data;
        if (tplist.length <= 0) {
            return history.push("/workspace/notfound");
        }

        setPartyList(tplist);
        _partyList = tplist;
        
        const storedPartyId = localStorage.getItem("party");
        
        if (storedPartyId) {
            const result1 = tplist.filter(
                (item: IParty) => item.id === storedPartyId
            );

            if(result1[0]) {
                _partyList = result1;
            }
        }

        appData.setSelectedParty(_partyList[0]);
        
        if (!skipWorkspaceSwitch) {
            await getIndustryList(_partyList[0].id);
        }
    };

    const getIndustryList = async (partyid: string) => {
        
        const requestKey = 'industry-list';
        const requestVersion = requestVersions.current.getNextVersion(requestKey);
        
        if (operationInProgress.current.industry) {
            return;
        }
        
        operationInProgress.current.industry = true;
        setIsLoadingIndustry(true);
        
        try {
            setParentChannelList([]);
            setEnvironmentList([]);
            setSelectedParentChannel(null);
            setSelectedEnvironment(null);
            
            const api_base_url = AppSettings.GMS_BACKEND_API_URL;
            let industryResponse = await ServiceWrapper.doGet(
                api_base_url + "party/" + partyid + "/industry/list/active",
                null,
                null,
                AuthManager.getGmsToken()
            );
            
            
            if (!requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                console.log('[getIndustryList] Not latest version, discarding');
                return;
            }
            
            if (industryResponse.errorfound) {
                console.log('[getIndustryList] Error found in response');
                throw new Error("Industry not found");
            }
            
            var tplist = industryResponse.data.data;
            if (tplist === undefined || tplist.length === 0) {
                setIndustryList([]);
                appData.setSelectedIndustry(null);
                
                localStorage.removeItem("industry");
                localStorage.removeItem("selectedParentChannel");
                localStorage.removeItem("selectedEnvironment");
                
                return history.push("/workspace/industry/notfound");
            }
            
            const storedIndustry = localStorage.getItem("industry");
            const storedParty = localStorage.getItem("party");
            
            let _industryUpdateData = tplist[0];
            let _partyId = partyid;
            let _industryId = tplist[0].id;
            
            if(storedIndustry && storedParty) {
                let finalData = JSON.parse(storedIndustry.replace(/\\/g, ""));
                if (!finalData) return;
                const test = tplist.filter(
                    (item: { id: string }) => item.id === finalData.id
                );
                
                if (test[0]) {
                    _industryUpdateData = test[0];
                    _partyId = storedParty;
                    _industryId = finalData.id;
                }
            }
            
            if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                setIndustryList(tplist);
                appData.update_industry(_industryUpdateData);
                
                if (!isSwitchingWorkspace.current) {
                    await getParentChannels(_partyId, _industryId);
                }
                
                const storedParentChannel = localStorage.getItem("selectedParentChannel");
                const storedEnvironment = localStorage.getItem("selectedEnvironment");
                let parentChannelId = '';
                let channelId = '';
                
                if (storedParentChannel) {
                    const parsedChannel = JSON.parse(storedParentChannel);
                    parentChannelId = parsedChannel.id || '';
                }
                
                if (storedEnvironment) {
                    const parsedEnv = JSON.parse(storedEnvironment);
                    channelId = parsedEnv.id || '';
                }
                
                isSwitchingWorkspace.current = true;
                
                if (
                    await switchWorkspace(
                        _partyId,
                        _industryId,
                        parentChannelId,
                        channelId,
                        "Please wait while we prepare your workspace..."
                    )
                ) {
                    history.push(
                        window.location.pathname === "/workspace/preparing"
                            ? "/gms/performance"
                            : window.location.pathname
                    );
                }
                
                isSwitchingWorkspace.current = false;
            }
            
        } catch (err) {
            if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                appData.setSelectedIndustry(null);
                setIndustryList([]);
                setParentChannelList([]);
                setEnvironmentList([]);
                setSelectedParentChannel(null);
                setSelectedEnvironment(null);
                
                localStorage.removeItem("industry");
                localStorage.removeItem("selectedParentChannel");
                localStorage.removeItem("selectedEnvironment");
                
                isSwitchingWorkspace.current = false;

                if (window.location.pathname === "/gms/performance") {
                    history.push("/gms/performance");
                } else {
                    history.push(window.location.pathname);
                }
            }
        } finally {
            operationInProgress.current.industry = false;
            setIsLoadingIndustry(false);
        }
    };

    const getParentChannels = async (partyId: string, industryId: string, shouldSwitch: boolean = false) => {
        
        const requestKey = `channels-${partyId}-${industryId}`;
        const requestVersion = requestVersions.current.getNextVersion(requestKey);
        
        if (operationInProgress.current.channel) {
            return;
        }
        
        operationInProgress.current.channel = true;
        setIsLoadingChannel(true);
        
        try {
            setEnvironmentList([]);
            setSelectedEnvironment(null);
            
            const parentResponse = await ServiceWrapper.doGet(
                `${AppSettings.GMS_BACKEND_API_URL}list/party/${partyId}/industry/${industryId}/parent_channel`,
                null,
                null,
                AuthManager.getGmsToken()
            );
            
            if (!requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                console.log('[getParentChannels] Not latest version, discarding');
                return;
            }
            
            const response = parentResponse?.data;
            
            if (response && response.data && Array.isArray(response.data)) {
                if (response.data.length === 0) {
                    setParentChannelList([]);
                    setEnvironmentList([]);
                    setSelectedParentChannel(null);
                    setSelectedEnvironment(null);
                    localStorage.removeItem("selectedParentChannel");
                    localStorage.removeItem("selectedEnvironment");
                    return;
                }
                
                if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                    setParentChannelList(response.data);
                    
                    const storedChannel = localStorage.getItem("selectedParentChannel");
                    let channelToSelect = null;
                    
                    if (storedChannel) {
                        try {
                            const parsedChannel = JSON.parse(storedChannel);
                            const foundChannel = response.data.find((ch: any) => ch.id === parsedChannel.id);
                            if (foundChannel) {
                                channelToSelect = foundChannel;
                            } else {
                                localStorage.removeItem("selectedParentChannel");
                                localStorage.removeItem("selectedEnvironment");
                            }
                        } catch (e) {
                            console.error('[getParentChannels] Error parsing stored channel:', e);
                            localStorage.removeItem("selectedParentChannel");
                            localStorage.removeItem("selectedEnvironment");
                        }
                    }
                    
                    if (!channelToSelect && response.data.length > 0) {
                        channelToSelect = response.data[0];
                        localStorage.setItem("selectedParentChannel", JSON.stringify(channelToSelect));
                    }
                    
                    if (channelToSelect) {
                        setSelectedParentChannel(channelToSelect);
                        await getEnvironments(channelToSelect.id, shouldSwitch, partyId);
                    }
                }
            } else {
                if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                    if (response?.message) {
                        toast.error(response.message);
                    }
                    setParentChannelList([]);
                    setEnvironmentList([]);
                    setSelectedParentChannel(null);
                    setSelectedEnvironment(null);
                    localStorage.removeItem("selectedParentChannel");
                    localStorage.removeItem("selectedEnvironment");
                }
            }
        } catch (error) {
            console.error('[getParentChannels] Error:', error);
            if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                setParentChannelList([]);
                setEnvironmentList([]);
                setSelectedParentChannel(null);
                setSelectedEnvironment(null);
                localStorage.removeItem("selectedParentChannel");
                localStorage.removeItem("selectedEnvironment");
            }
        } finally {
            operationInProgress.current.channel = false;
            setIsLoadingChannel(false);
        }
    };

    const getEnvironments = async (parentChannelId: string, shouldSwitch: boolean = false, partyId?: string) => {
        
        const requestKey = `environments-${parentChannelId}`;
        const requestVersion = requestVersions.current.getNextVersion(requestKey);
        
        if (operationInProgress.current.environment) {
            return;
        }
        
        operationInProgress.current.environment = true;
        setIsLoadingEnvironment(true);
        
        try {
            const targetPartyId = partyId || appData.selectedParty?.id;
            
            if (!targetPartyId) {
                setEnvironmentList([]);
                setSelectedEnvironment(null);
                return;
            }
            
            const environementResponse = await ServiceWrapper.doGet(
                `${AppSettings.GMS_BACKEND_API_URL}list/party/${targetPartyId}/parent_channel/${parentChannelId}/channel`,
                null,
                null,
                AuthManager.getGmsToken()
            );
            
            
            if (!requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                return;
            }
            
            const response = environementResponse?.data;
            
            if (response && response.data && Array.isArray(response.data)) {
                if (response.data.length === 0) {
                    setEnvironmentList([]);
                    setSelectedEnvironment(null);
                    localStorage.removeItem("selectedEnvironment");
                    return;
                }
                
                if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                    setEnvironmentList(response.data);
                    
                    const storedEnv = localStorage.getItem("selectedEnvironment");
                    let envToSelect = null;
                    
                    if (storedEnv) {
                        try {
                            const parsedEnv = JSON.parse(storedEnv);
                            const foundEnv = response.data.find((env: any) => env.id === parsedEnv.id);
                            if (foundEnv) {
                                envToSelect = foundEnv;
                            } else {
                                localStorage.removeItem("selectedEnvironment");
                            }
                        } catch (e) {
                            console.error('[getEnvironments] Error parsing stored environment:', e);
                            localStorage.removeItem("selectedEnvironment");
                        }
                    }
                    
                    if (!envToSelect && response.data.length > 0) {
                        envToSelect = response.data[0];
                        localStorage.setItem("selectedEnvironment", JSON.stringify(envToSelect));
                    }
                    
                    if (envToSelect) {
                        setSelectedEnvironment(envToSelect);
                        appData.setChannelKey(envToSelect?.key ?? '');
                        
                        if (shouldSwitch && appData.selectedParty && appData.selectedIndustry) {
                            await switchWorkspace(
                                appData.selectedParty.id,
                                appData.selectedIndustry.id,
                                parentChannelId,
                                envToSelect.id,
                                "Updating workspace..."
                            );
                        }
                    }
                }
            } else {
                if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                    setEnvironmentList([]);
                    setSelectedEnvironment(null);
                    localStorage.removeItem("selectedEnvironment");
                }
            }
        } catch (error) {
            console.error('[getEnvironments] Error:', error);
            if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                setEnvironmentList([]);
                setSelectedEnvironment(null);
                localStorage.removeItem("selectedEnvironment");
            }
        } finally {
            operationInProgress.current.environment = false;
            setIsLoadingEnvironment(false);
        }
    };

    const switchWorkspace = async (
        party_id: string,
        industry_id: string,
        parent_channel_id: string = '',
        channel_id: string = '',
        dialogmessage: string = "Switching workspace...",
        initnew: boolean = false
    ) => {
        const api_base_url = AppSettings.GMS_BACKEND_API_URL;
        const psresp = await ServiceWrapper.doPost(
            api_base_url + "party/switch",
            { 
                party_id: party_id, 
                industry_id: industry_id, 
                app_name: "GMS", 
                parent_channel_id: parent_channel_id || '', 
                channel_id: channel_id || ''
            } as any,
            null,
            AuthManager.getGmsToken()
        );

        if (!psresp.errorfound && psresp.data.done) {
            localStorage.setItem(
                "parties",
                JSON.stringify(psresp.data.user_object.profile.parties || "")
            );
            localStorage.setItem(
                "current_party_id",
                JSON.stringify(psresp.data.user_object.current_party_id || "")
            );
        }

        try{
            if (psresp.errorfound) 
                throw new Error("<p>We are Sorry, We could not set or switch the workspace for you.</p><p><strong>" +
                psresp.data.message +
                "</strong></p>") 
            
            if(!psresp.data.done)
                throw new Error( "<p>We are Sorry, We could not set or switch the workspace for you.</p><p><strong>There was a technical error.</strong></p>")
            
            if (partyList && partyList.length > 0) {
                const partyDetails = partyList?.filter(
                    (item: { id: string }) => item.id === party_id
                );
                
                if (partyDetails && partyDetails[0]) {
                    appData.update_party(partyDetails[0]);
                } 
            }
            
            await AuthManager.setGmsToken(psresp.data.access_token);
            if (!Utils.isUndefinedOrNull(psresp.data.user_object))
                await AuthManager.setAuthUser(psresp.data.user_object);
            const pm = new PermissionsManager();
            await pm.ProcessPermissions();
            return true;
        }
        catch(err: any) {
            console.error('[switchWorkspace] Error:', err);
            BootBoxManager.dialog(
                "Workspace switch Failed",
                err.message
            );
            history.push("/workspace/industry/notfound");
            return false;
        }
    };

    const handlePartyChange = async (item: any) => {
        if (operationInProgress.current.party || isLoadingParty || isLoadingIndustry) {
            return;
        }
        
        analyticsContext?.clearAllTheFields();
        appData.setChannelKey("");
        if(!item) return toast.error("Party not found")
        
        operationInProgress.current.party = true;
        setIsLoadingParty(true);
        isManualPartyChange.current = true;
        
        history.push("/workspace/preparing");
        
        localStorage.removeItem("industry");
        localStorage.removeItem("selectedParentChannel");
        localStorage.removeItem("selectedEnvironment");
        
        localStorage.setItem("party", item.id);
        localStorage.setItem("current_party_id", JSON.stringify(item.id || ""));
        
        appData.setSelectedParty(item);
        setIndustryList([]);
        setParentChannelList([]);
        setEnvironmentList([]);
        setSelectedParentChannel(null);
        setSelectedEnvironment(null);
        
        await getIndustryList(item.id);
        
        isManualPartyChange.current = false;
        setIsLoadingParty(false);
        operationInProgress.current.party = false;
    };

    const handleIndustryChange = async (item: any) => {
        if (operationInProgress.current.industry || isLoadingIndustry || isLoadingChannel) {
            return;
        }
        
        analyticsContext?.clearAllTheFields();
        
        operationInProgress.current.industry = true;
        setIsLoadingIndustry(true);
        isManualIndustryChange.current = true;
        
        history.push("/workspace/preparing");
        appData.update_industry(item);
        
        localStorage.removeItem("selectedParentChannel");
        localStorage.removeItem("selectedEnvironment");
        setParentChannelList([]);
        setEnvironmentList([]);
        setSelectedParentChannel(null);
        setSelectedEnvironment(null);
        
        localStorage.setItem("industry", JSON.stringify(item));
        localStorage.setItem("party", appData.selectedParty.id);
        
        await getParentChannels(appData.selectedParty.id, item.id, true);
        
        history.push("/gms/performance");
        
        isManualIndustryChange.current = false;
        setIsLoadingIndustry(false);
        operationInProgress.current.industry = false;
    };

    const handleParentChannelChange = async (item: any) => {
        if (operationInProgress.current.channel || isLoadingChannel || isLoadingEnvironment) {
            return;
        }
        
        if (!item) return;
        
        operationInProgress.current.channel = true;
        setIsLoadingChannel(true);
        
        setSelectedParentChannel(item);
        localStorage.setItem("selectedParentChannel", JSON.stringify(item));
        
        localStorage.removeItem("selectedEnvironment");
        setEnvironmentList([]);
        setSelectedEnvironment(null);
        
        await getEnvironments(item.id, true, appData.selectedParty?.id);
        
        appData.setSelectedChannel?.(item);
        setIsLoadingChannel(false);
        operationInProgress.current.channel = false;
    };

    const handleEnvironmentChange = async (item: any) => {
        if (operationInProgress.current.environment || isLoadingEnvironment) {
            return;
        }
        
        if (!item) return;
        
        operationInProgress.current.environment = true;
        setIsLoadingEnvironment(true);
        
        setSelectedEnvironment(item);
        localStorage.setItem("selectedEnvironment", JSON.stringify(item));
        
        appData.setSelectedEnvironment?.(item);
        appData.setChannelKey(item?.key ?? '');
        
        if (appData.selectedParty && appData.selectedIndustry && selectedParentChannel) {
            const success = await switchWorkspace(
                appData.selectedParty.id,
                appData.selectedIndustry.id,
                selectedParentChannel.id,
                item.id,
                "Switching to environment: " + item.name
            );
            
            if (success) {
                toast.success(`Switched to environment "${item.name}"`);
                if (window.location.pathname !== "/gms/performance") {
                    history.push("/gms/performance");
                }
            }
        }
        
        setIsLoadingEnvironment(false);
        operationInProgress.current.environment = false;
    };

    const getChannelList = async () => {
        try {
            const response = await getMerchantsBasedOnPartyAndIndustry(current_party_id, industryId);
            if (response?.data?.done) {
                const channels = response?.data.data?.map((res: any) => ({
                    label: res?.user_name,
                    value: res?.merchant_key
                })) ?? [];
                setChannelList(channels)
                setSelectedChannel(channels[0]?.value)
                appData.setChannelKey(channels[0]?.value);
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleChannelChange = (e:string)=>{
        setSelectedChannel(e)
        appData.setChannelKey(e)
    }

    useEffect(() => {
        
        if (isManualPartyChange.current || isInitialLoad.current || partyList.length <= 0) return;
        
        async function fetchIndustryList() {
            let _party = partyList
            const storedPartyId = localStorage.getItem("party");
            
            if (storedPartyId) {
                const result1 = partyList.filter(
                    (item: any) => item.id === storedPartyId
                ) as any;
                
                if(result1[0]){
                    setIndustryList([]);
                    _party = result1
                }

                appData.setSelectedParty(_party[0]);
                await getIndustryList(_party[0].id);
            }
        }
        fetchIndustryList()
    }, [partyList])

    useEffect(() => {
        if (current_party_id && industryId) {
            appData.setChannelKey("");
            getChannelList();
        }
    }, [current_party_id, industryId]);

    React.useEffect(() => {
        return () => {
            requestVersions.current.clearAll();
        };
    }, []);
    
    return {
        partyList,
        handlePartyChange,
        industryList,
        handleIndustryChange,
        getUserName,
        channelList,
        selectedChannel,
        handleChannelChange,
        parentChannelList,
        selectedParentChannel,
        handleParentChannelChange,
        environmentList,
        selectedEnvironment,
        handleEnvironmentChange,
        refreshParties,
        refreshDomains,
        refreshChannels,
        refreshEnvironments,
        isLoadingParty,
        isLoadingIndustry,
        isLoadingChannel,
        isLoadingEnvironment
    }
}

export default useSearchBar;

class RequestVersionManager {
    private versions: Map<string, number> = new Map();
    
    getNextVersion(key: string): number {
        const currentVersion = this.versions.get(key) || 0;
        const nextVersion = currentVersion + 1;
        this.versions.set(key, nextVersion);
        return nextVersion;
    }
    
    isLatestVersion(key: string, version: number): boolean {
        return this.versions.get(key) === version;
    }
    
    clear(key: string) {
        this.versions.delete(key);
    }
    
    clearAll() {
        this.versions.clear();
    }
}